import { formatPatientName } from "./administrationRules";

export function csvCell(value) {
    return `"${String(value ?? "").replaceAll("\"", "\"\"")}"`;
}

export function htmlEscape(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll("\"", "&quot;");
}

export function reportPatientLabel(row) {
    const name = formatPatientName(row);
    const details = [row.chambre ? `Chambre ${row.chambre}` : "", row.productionSubcategory || ""]
        .filter(Boolean)
        .join(" - ");
    return details ? `${name} (${details})` : name;
}

export function buildAdministrationReportRows(candidates) {
    return (candidates || []).flatMap(patient => (patient.issues || []).map((issue, index) => ({
        key: `${patient.patientId}-${issue.type}-${index}`,
        patientId: patient.patientId,
        patientName: reportPatientLabel(patient),
        issueType: issue.type,
        message: issue.message,
        included: true,
    })));
}

export function generateAdministrationReportHTML({ ehpadName, rows }) {
    const now = new Date().toLocaleString("fr-FR");
    const bodyRows = rows
        .filter(row => row.included)
        .map(row => `
            <tr>
                <td>${htmlEscape(row.patientName)}</td>
                <td>${htmlEscape(row.message)}</td>
            </tr>
        `)
        .join("");
    return `<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Rapport administratif - ${htmlEscape(ehpadName)}</title>
    <style>
        @page { margin: 1.5cm; }
        body { font-family: Arial, sans-serif; color: #111827; font-size: 12px; }
        h1 { font-size: 18px; margin: 0 0 6px; }
        .meta { color: #4b5563; margin-bottom: 18px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #374151; padding: 7px 8px; vertical-align: top; }
        th { background: #f3f4f6; text-align: left; font-weight: 700; }
        tr { break-inside: avoid; page-break-inside: avoid; }
    </style>
</head>
<body>
    <h1>Demandes administratives - ${htmlEscape(ehpadName || "EHPAD")}</h1>
    <div class="meta">Édité le ${htmlEscape(now)} - ${rows.filter(row => row.included).length} demande(s)</div>
    <table>
        <thead>
            <tr>
                <th style="width: 32%;">Résident</th>
                <th>Demande à l'EHPAD</th>
            </tr>
        </thead>
        <tbody>${bodyRows || `<tr><td colspan="2">Aucune demande à transmettre.</td></tr>`}</tbody>
    </table>
</body>
</html>`;
}

export function openAdministrationReportPrint({ ehpadName, rows }) {
    const html = generateAdministrationReportHTML({ ehpadName, rows });
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = globalThis.open(url, "_blank", "width=900,height=700");
    if (!win) {
        URL.revokeObjectURL(url);
        return;
    }
    win.addEventListener("load", () => {
        URL.revokeObjectURL(url);
        win.focus();
        win.print();
    }, { once: true });
}

export function downloadImportReport(report) {
    const rows = [
        ["Gravité", "Feuille", "Ligne", "Résident", "Message"],
        ...(report.issues || []).map(issue => [
            issue.severity,
            issue.sheet,
            issue.row,
            issue.resident,
            issue.message,
        ]),
    ];
    const csv = `\uFEFF${rows.map(row => row.map(csvCell).join(";")).join("\r\n")}`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().replaceAll(":", "-").slice(0, 19);
    link.href = url;
    link.download = `rapport-import-administration-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

export function importReportSummary(report) {
    if (report.applied) {
        if (report.issues?.some(issue => issue.severity === "ERROR")) {
            return `Import terminé : ${report.rowsImported} ligne(s) importée(s), lignes en erreur ignorées.`;
        }
        return `Import terminé : ${report.rowsImported} ligne(s) importée(s).`;
    }
    if (report.dryRun) {
        return `Contrôle terminé : ${report.rowsReady} ligne(s) prête(s) sur ${report.rowsRead}.`;
    }
    return `Import terminé : ${report.rowsImported} ligne(s) importée(s), lignes en erreur ignorées.`;
}
